import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Modal, Field, Input, Select, TextArea, Button, EmptyState } from "@/admin/ui";
import { toast } from "sonner";
import { PageHeader, StatusPill, PriorityPill } from "@/user/workspace/wsui";
import { ws, fmtDate } from "@/user/workspace/ws.api";

const EMPTY = { title: "", description: "", priority: "medium", status: "open", due_date: "" };

export default function Tasks() {
    const { user } = useOutletContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => { setLoading(true); ws.listTasks().then(setRows).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);

    async function create() {
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        setBusy(true);
        try {
            await ws.createTask(form);
            toast.success("Task added");
            setOpen(false); setForm(EMPTY); load();
        } catch { toast.error("Could not add task"); }
        finally { setBusy(false); }
    }

    async function cycleStatus(t) {
        const next = t.status === "open" ? "in progress" : t.status === "in progress" ? "done" : "open";
        try { await ws.updateTask(t.id, { status: next }); load(); }
        catch { toast.error("Update failed"); }
    }

    async function remove(t) {
        if (!window.confirm("Delete this task?")) return;
        try { await ws.deleteTask(t.id); load(); } catch { toast.error("Delete failed"); }
    }

    return (
        <div data-testid="page-tasks">
            <PageHeader
                eyebrow={user.name?.split(" ")[0] || "You"}
                title="Tasks"
                subtitle="Your personal to-dos across the department."
                actions={<Button variant="primary" onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="task-new">New Task</Button>}
            />

            {loading ? (
                <div className="text-neutral-500 py-16 text-center">Loading…</div>
            ) : rows.length === 0 ? (
                <EmptyState title="No tasks yet" hint="Add your first task to stay on top of your day." />
            ) : (
                <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden" data-testid="task-list">
                    {rows.map((t) => (
                        <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center justify-between gap-4 px-4 sm:px-5 py-3.5">
                            <div className="min-w-0">
                                <div className={`text-[14px] font-medium truncate ${t.status === "done" ? "line-through text-neutral-400" : "text-black"}`}>{t.title}</div>
                                {t.description && <div className="text-[12px] text-neutral-500 truncate mt-0.5">{t.description}</div>}
                                <div className="text-[11px] text-neutral-400 mt-0.5">{t.due_date ? `Due ${t.due_date}` : `Added ${fmtDate(t.created_at)}`}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <PriorityPill priority={t.priority} />
                                <button onClick={() => cycleStatus(t)} data-testid={`task-status-${t.id}`}><StatusPill status={t.status} /></button>
                                <button onClick={() => remove(t)} data-testid={`task-delete-${t.id}`} className="text-rose-500 hover:text-rose-600 text-[12px]">Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={open} onClose={() => setOpen(false)} title="New Task" size="md" testid="task-modal"
                footer={<>
                    <Button variant="ghost" onClick={() => setOpen(false)} data-testid="task-cancel">Cancel</Button>
                    <Button variant="primary" onClick={create} disabled={busy} data-testid="task-save">{busy ? "Saving…" : "Add Task"}</Button>
                </>}>
                <div className="space-y-4">
                    <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></Field>
                    <Field label="Description"><TextArea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc" /></Field>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Priority">
                            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="task-priority">
                                {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                            </Select>
                        </Field>
                        <Field label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" /></Field>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
